// Composites for /sewing-primitives: whole objects assembled from the lab's
// primitives, plus the attachment vocabulary that joins them.
//
// The teddy bear is the flagship: it exercises every joint type at once
// (ears sewn into a seam, limbs set into openings or ladder-stitched on),
// which is why it's the classic first "real" plush. A composite model uses
// the same interface FoldViewer renders, but here t means ASSEMBLY: t = 0 is
// the exploded parts cloud, t = 1 is the finished bear. Attachment points
// are marker dots — one riding each part — that converge as it assembles;
// their color says which joining technique to use.

import { buildShape } from "./sewingShapes";

/* ============================================================
   Joint vocabulary
   ============================================================ */

export const JOINTS = [
  {
    id: "inseam",
    name: "sewn into a seam",
    color: "#f43f5e",
    blurb:
      "The part is sandwiched between the parent's layers BEFORE sewing, pointing inward; turn the parent right-side out and it pops out attached. How ears and tails work.",
  },
  {
    id: "socket",
    name: "set into an opening",
    color: "#0ea5e9",
    blurb:
      "Cut an opening in the parent and sew the part's rim into it — the rim and the opening must be the same length (the 2πr rule, between two objects).",
  },
  {
    id: "ladder",
    name: "ladder stitch",
    color: "#22c55e",
    blurb:
      "Both parts are finished and stuffed first, then joined with an invisible hand stitch. The default way to attach anything after the fact.",
  },
  {
    id: "applique",
    name: "appliqué",
    color: "#a855f7",
    blurb:
      "The honorary fourth: sewn flat onto the surface, little or no stuffing. For details that should read as drawn on — doors, buttons, spots, faces.",
  },
];

export const JOINT_MAP = Object.fromEntries(JOINTS.map((j) => [j.id, j]));

/* ============================================================
   Small transform helpers (scale → rotX → rotZ → translate)
   ============================================================ */

export const deg = (a) => (a * Math.PI) / 180;

export const transformPoint = (p, { scale = [1, 1, 1], rx = 0, rz = 0, ry = 0 }) => {
  let [x, y, z] = [p[0] * scale[0], p[1] * scale[1], p[2] * scale[2]];
  if (rx) {
    const c = Math.cos(rx);
    const s = Math.sin(rx);
    [y, z] = [y * c - z * s, y * s + z * c];
  }
  if (rz) {
    const c = Math.cos(rz);
    const s = Math.sin(rz);
    [x, y] = [x * c - y * s, x * s + y * c];
  }
  if (ry) {
    const c = Math.cos(ry);
    const s = Math.sin(ry);
    [x, z] = [x * c + z * s, -x * s + z * c];
  }
  return [x, y, z];
};

// Param keys that are physical dimensions (cm) and therefore scale with the
// composite's size dial. Counts (gores) and tessellation (segments, rows)
// must NOT scale — a 2× teddy has bigger gores, not more of them.
const DIM_KEYS = ["radius", "height", "width", "depth", "base"];

const scaleParams = (params, k) =>
  Object.fromEntries(
    Object.entries(params).map(([key, v]) => [key, DIM_KEYS.includes(key) ? v * k : v])
  );

/* ============================================================
   Composite builder
   ============================================================ */

// spec.parts: [{ id, name, shape, params, recenter, scale, rx, rz, ry, at, tone, note }]
// spec.attachments: [{ joint, a, b, pos, label }]  (pos in assembled coords)
// spec.center / spec.explode control the exploded layout.
// `size` is a uniform multiplier — the seed of the creature generator: every
// dimension (and therefore every pattern measurement) scales together.
export const buildComposite = (spec, size = 1) => {
  const explodeK = spec.explode ?? 0.9;
  const center = (spec.center ?? [0, 0, 0]).map((c) => c * size);

  const parts = spec.parts.map((part) => {
    const params = scaleParams(part.params, size);
    const source = buildShape(part.shape, params);
    const sewn = source.positionsAt(1);
    const rc = (part.recenter ?? [0, 0, 0]).map((c) => c * size);
    const local = sewn.map((p) =>
      transformPoint(
        [p[0] - rc[0], p[1] - rc[1], p[2] - rc[2]],
        {
          scale: part.scale,
          rx: deg(part.rx || 0),
          rz: deg(part.rz || 0),
          ry: deg(part.ry || 0),
        }
      )
    );
    const at = part.at.map((c) => c * size);
    const explodedAt = at.map((c, i) => c + (c - center[i]) * explodeK);
    return { ...part, params, at, local, explodedAt, faces: source.faces };
  });

  const partBase = [];
  let vertCount = 0;
  parts.forEach((p) => {
    partBase.push(vertCount);
    vertCount += p.local.length;
  });
  const partIndex = Object.fromEntries(parts.map((p, i) => [p.id, i]));

  const positionsAt = (t) => {
    const pts = [];
    parts.forEach((part) => {
      const tx = part.at.map((c, i) => part.explodedAt[i] + (c - part.explodedAt[i]) * t);
      part.local.forEach((p) => pts.push([p[0] + tx[0], p[1] + tx[1], p[2] + tx[2]]));
    });
    // Attachment markers: one point riding each of the two joined parts.
    (spec.attachments || []).forEach((att) => {
      const pos = att.pos.map((c) => c * size);
      [att.a, att.b].forEach((partId) => {
        const part = parts[partIndex[partId]];
        const drift = part.at.map((c, i) => (part.explodedAt[i] - c) * (1 - t));
        pts.push([pos[0] + drift[0], pos[1] + drift[1], pos[2] + drift[2]]);
      });
    });
    return pts;
  };

  const faces = [];
  parts.forEach((part, pi) => {
    part.faces.forEach((f) => {
      faces.push({ verts: f.verts.map((v) => v + partBase[pi]), piece: part.id });
    });
  });

  const markers = (spec.attachments || []).flatMap((att, ai) => {
    const joint = JOINT_MAP[att.joint];
    return [0, 1].map((k) => ({
      vert: vertCount + ai * 2 + k,
      color: joint.color,
      label: att.label,
    }));
  });

  return {
    positionsAt,
    faces,
    seams: [],
    folds: [],
    markers,
    pieceTones: Object.fromEntries(parts.map((p) => [p.id, p.tone])),
    parts: parts.map(({ id, name, shape, params, note }) => ({ id, name, shape, params, note })),
    attachments: (spec.attachments || []).map((att) => ({
      ...att,
      jointName: JOINT_MAP[att.joint].name,
      color: JOINT_MAP[att.joint].color,
    })),
    insight: spec.insight,
  };
};

/* ============================================================
   The teddy bear
   ============================================================ */

const FUR = [32, 55];
const CREAM = [42, 75];

export const buildTeddy = (size = 1) =>
  buildComposite(
    {
    center: [0, 8, 0],
    explode: 0.85,
    insight:
      "Nothing here is new geometry — a bear is spheres and cylinders wearing a trench coat. The new skill is the joints: each dot color is a different way of attaching.",
    parts: [
      {
        id: "body",
        name: "body",
        shape: "sphere",
        params: { radius: 5, gores: 6, rows: 8 },
        recenter: [0, 5, 0],
        scale: [1, 1.22, 0.85],
        at: [0, 6, 0],
        tone: FUR,
        note: "a gored ball, squashed taller than wide",
      },
      {
        id: "head",
        name: "head",
        shape: "sphere",
        params: { radius: 3.6, gores: 6, rows: 8 },
        recenter: [0, 3.6, 0],
        at: [0, 14, 0.3],
        tone: FUR,
        note: "a smaller gored ball",
      },
      {
        id: "muzzle",
        name: "muzzle",
        shape: "sphere",
        params: { radius: 1.6, gores: 5, rows: 6 },
        recenter: [0, 1.6, 0],
        scale: [1, 0.78, 0.85],
        at: [0, 13.4, 3.3],
        tone: CREAM,
        note: "a tiny squashed ball in the contrast fabric",
      },
      {
        id: "earL",
        name: "ear",
        shape: "sphere",
        params: { radius: 1.5, gores: 5, rows: 6 },
        recenter: [0, 1.5, 0],
        scale: [1, 1, 0.45],
        at: [-2.5, 17.5, 0.2],
        tone: FUR,
        note: "flattened half-balls — only the top half shows",
      },
      {
        id: "earR",
        name: "ear",
        shape: "sphere",
        params: { radius: 1.5, gores: 5, rows: 6 },
        recenter: [0, 1.5, 0],
        scale: [1, 1, 0.45],
        at: [2.5, 17.5, 0.2],
        tone: FUR,
      },
      {
        // Shoulder ends must EMBED in the torso: the body ellipsoid narrows
        // fast above its equator, so arms hang from ~(±3, 10.3) and flare
        // outward, not from the widest point (they float there).
        id: "armL",
        name: "arm",
        shape: "cylinder",
        params: { radius: 1.2, height: 5.5, segments: 18 },
        recenter: [0, 1.2, 0],
        rx: 75,
        rz: -25,
        at: [-4.1, 8.0, 1.0],
        tone: FUR,
        note: "cylinders — lesson 3, at plush-limb scale",
      },
      {
        id: "armR",
        name: "arm",
        shape: "cylinder",
        params: { radius: 1.2, height: 5.5, segments: 18 },
        recenter: [0, 1.2, 0],
        rx: 75,
        rz: 25,
        at: [4.1, 8.0, 1.0],
        tone: FUR,
      },
      {
        id: "legL",
        name: "leg",
        shape: "cylinder",
        params: { radius: 1.4, height: 5.5, segments: 14 },
        recenter: [0, 1.4, 0],
        rx: 90,
        at: [-2.4, 2.75, 0.8],
        tone: FUR,
      },
      {
        id: "legR",
        name: "leg",
        shape: "cylinder",
        params: { radius: 1.4, height: 5.5, segments: 14 },
        recenter: [0, 1.4, 0],
        rx: 90,
        at: [2.4, 2.75, 0.8],
        tone: FUR,
      },
    ],
    attachments: [
      { joint: "ladder", a: "head", b: "body", pos: [0, 11.2, 0.4], label: "head → body" },
      { joint: "ladder", a: "muzzle", b: "head", pos: [0, 13.3, 2.5], label: "muzzle → head" },
      { joint: "inseam", a: "earL", b: "head", pos: [-2.4, 16.6, 0.2], label: "ear → head seam" },
      { joint: "inseam", a: "earR", b: "head", pos: [2.4, 16.6, 0.2], label: "ear → head seam" },
      { joint: "ladder", a: "armL", b: "body", pos: [-3.4, 10.1, 0.5], label: "arm → body" },
      { joint: "ladder", a: "armR", b: "body", pos: [3.4, 10.1, 0.5], label: "arm → body" },
      { joint: "socket", a: "legL", b: "body", pos: [-2.4, 5.3, 0.7], label: "leg → body opening" },
      { joint: "socket", a: "legR", b: "body", pos: [2.4, 5.3, 0.7], label: "leg → body opening" },
    ],
    },
    size
  );

/* ============================================================
   The rest of the starter menu, as viewable 3D models.
   Ids match COMPOSITES in sewingCurriculum.js.
   ============================================================ */

const CONE_TONE = [35, 70]; // waffle
const SCOOP = [330, 55];
const STEM = [45, 30];
const CAP = [0, 65];
const CERAMIC = [200, 45];
const STEEL = [220, 15];
const CUSHION = [150, 45];
const ROCKET_BODY = [0, 0];
const ROCKET_HOT = [15, 80];

const buildIceCream = (size = 1) =>
  buildComposite(
    {
      center: [0, 7, 0],
      explode: 0.8,
      insight:
        "Two lessons stacked: the cone (tip-down this time — geometry doesn't care which way is up) and a gored ball, ladder-stitched together at the rim.",
      parts: [
        {
          id: "cone",
          name: "cone",
          shape: "cone",
          params: { radius: 3.2, height: 9 },
          recenter: [0, 4.5, 0],
          rx: 180,
          at: [0, 4.5, 0],
          tone: CONE_TONE,
          note: "the lesson-4 cone, flipped tip-down",
        },
        {
          id: "scoop",
          name: "scoop",
          shape: "sphere",
          params: { radius: 3.6, gores: 6, rows: 8 },
          recenter: [0, 3.6, 0],
          at: [0, 10.6, 0],
          tone: SCOOP,
          note: "a 6-gore ball, strawberry recommended",
        },
      ],
      attachments: [
        { joint: "ladder", a: "scoop", b: "cone", pos: [0, 9.6, 2.4], label: "scoop → cone rim" },
      ],
    },
    size
  );

const buildMushroom = (size = 1) =>
  buildComposite(
    {
      center: [0, 4, 0],
      explode: 0.9,
      insight:
        "A squashed gored ball on a fat cylinder. In real fabric the cap gets a circle sewn underneath (the gills) so the stuffing stays in — see the plan below.",
      parts: [
        {
          id: "stem",
          name: "stem",
          shape: "cylinder",
          params: { radius: 2.2, height: 5, segments: 20 },
          recenter: [0, 2.2, 0],
          rx: 90,
          at: [0, 2.5, 0],
          tone: STEM,
          note: "a short fat can, stood upright",
        },
        {
          id: "cap",
          name: "cap",
          shape: "sphere",
          params: { radius: 4.8, gores: 8, rows: 8 },
          recenter: [0, 4.8, 0],
          scale: [1, 0.55, 1],
          at: [0, 5.2, 0],
          tone: CAP,
          note: "a gored ball squashed to 55% height — appliqué the spots",
        },
      ],
      attachments: [
        { joint: "ladder", a: "cap", b: "stem", pos: [0, 4.4, 2.0], label: "stem → cap underside" },
      ],
    },
    size
  );

const buildMug = (size = 1) =>
  buildComposite(
    {
      center: [0, 4, 0],
      explode: 0.9,
      insight:
        "The lesson-3 can, upright. The handle is shown as three short tubes so you can see the shape — in fabric you'd sew ONE thin tube and bend it into the C.",
      parts: [
        {
          id: "body",
          name: "body",
          shape: "cylinder",
          params: { radius: 3.5, height: 8, segments: 24 },
          recenter: [0, 3.5, 0],
          rx: 90,
          at: [0, 4, 0],
          tone: CERAMIC,
          note: "one wall + two lids (leave the top lid off if you're brave)",
        },
        {
          id: "handleTop",
          name: "handle",
          shape: "cylinder",
          params: { radius: 0.9, height: 2.6, segments: 10 },
          recenter: [0, 0.9, 0],
          ry: 90,
          at: [4.7, 6.2, 0],
          tone: CERAMIC,
          note: "really one bent tube — ladder-stitched at both ends",
        },
        {
          id: "handleSide",
          name: "handle",
          shape: "cylinder",
          params: { radius: 0.9, height: 3.2, segments: 10 },
          recenter: [0, 0.9, 0],
          rx: 90,
          at: [6.0, 4.5, 0],
          tone: CERAMIC,
        },
        {
          id: "handleBot",
          name: "handle",
          shape: "cylinder",
          params: { radius: 0.9, height: 2.6, segments: 10 },
          recenter: [0, 0.9, 0],
          ry: 90,
          at: [4.7, 2.8, 0],
          tone: CERAMIC,
        },
      ],
      attachments: [
        { joint: "ladder", a: "handleTop", b: "body", pos: [3.6, 6.2, 0], label: "handle → body" },
        { joint: "ladder", a: "handleBot", b: "body", pos: [3.6, 2.8, 0], label: "handle → body" },
      ],
    },
    size
  );

const buildMicrowave = (size = 1) =>
  buildComposite(
    {
      center: [0, 3.5, 0],
      explode: 0.9,
      insight:
        "The lesson-1 box at microwave proportions. The door and buttons are appliqué — flat details sewn onto the surface, not built as 3D pieces.",
      parts: [
        {
          id: "body",
          name: "body",
          shape: "box",
          params: { width: 13, depth: 8, height: 8 },
          at: [0, 0, 0],
          tone: STEEL,
          note: "wide and shallow — same net, microwave numbers",
        },
        {
          id: "door",
          name: "door",
          shape: "box",
          params: { width: 8, depth: 0.9, height: 6.2 },
          at: [-1.9, 0.9, 4.1],
          tone: [220, 8],
          note: "a very flat box (or skip 3D entirely and appliqué a panel)",
        },
        {
          id: "btnA",
          name: "button",
          shape: "cylinder",
          params: { radius: 0.6, height: 0.8, segments: 10 },
          recenter: [0, 0.6, 0],
          at: [4.7, 5.6, 4.05],
          tone: ROCKET_HOT,
          note: "tiny cylinders — or felt circles appliquéd on",
        },
        {
          id: "btnB",
          name: "button",
          shape: "cylinder",
          params: { radius: 0.6, height: 0.8, segments: 10 },
          recenter: [0, 0.6, 0],
          at: [4.7, 4.2, 4.05],
          tone: ROCKET_HOT,
        },
        {
          id: "btnC",
          name: "button",
          shape: "cylinder",
          params: { radius: 0.6, height: 0.8, segments: 10 },
          recenter: [0, 0.6, 0],
          at: [4.7, 2.8, 4.05],
          tone: ROCKET_HOT,
        },
      ],
      attachments: [
        { joint: "applique", a: "door", b: "body", pos: [-1.9, 4, 4.4], label: "door → front" },
        { joint: "applique", a: "btnB", b: "body", pos: [4.7, 4.2, 4.3], label: "buttons → front" },
      ],
    },
    size
  );

const buildChair = (size = 1) =>
  buildComposite(
    {
      center: [0, 5, 0],
      explode: 0.9,
      insight:
        "Furniture is just boxes ladder-stitched into an argument about comfort. Squishy cylinders read as rolled armrests.",
      parts: [
        {
          id: "seat",
          name: "seat",
          shape: "box",
          params: { width: 10, depth: 8, height: 4 },
          at: [0, 0, 0],
          tone: CUSHION,
          note: "the base box — firm stuffing here",
        },
        {
          id: "back",
          name: "backrest",
          shape: "box",
          params: { width: 10, depth: 2.5, height: 8 },
          at: [0, 4, -2.75],
          tone: CUSHION,
          note: "tall thin box standing on the seat's rear edge",
        },
        {
          id: "armL",
          name: "armrest",
          shape: "cylinder",
          params: { radius: 1.4, height: 7, segments: 16 },
          recenter: [0, 1.4, 0],
          at: [-4.6, 5.4, 0.3],
          tone: CUSHION,
          note: "cylinders lying front-to-back",
        },
        {
          id: "armR",
          name: "armrest",
          shape: "cylinder",
          params: { radius: 1.4, height: 7, segments: 16 },
          recenter: [0, 1.4, 0],
          at: [4.6, 5.4, 0.3],
          tone: CUSHION,
        },
      ],
      attachments: [
        { joint: "ladder", a: "back", b: "seat", pos: [0, 4.3, -1.8], label: "backrest → seat" },
        { joint: "ladder", a: "armL", b: "seat", pos: [-4.4, 4.4, 1.2], label: "armrest → seat" },
        { joint: "ladder", a: "armR", b: "seat", pos: [4.4, 4.4, 1.2], label: "armrest → seat" },
      ],
    },
    size
  );

const buildRocket = (size = 1) =>
  buildComposite(
    {
      center: [0, 7, 0],
      explode: 0.8,
      insight:
        "The whole primitive kit in one object. The nose is a SOCKET joint: the cone's base circle and the body's top rim are both 2πr — they must agree.",
      parts: [
        {
          id: "body",
          name: "body",
          shape: "cylinder",
          params: { radius: 2.8, height: 10, segments: 24 },
          recenter: [0, 2.8, 0],
          rx: 90,
          at: [0, 6.5, 0],
          tone: ROCKET_BODY,
          note: "a tall can — the fuselage",
        },
        {
          id: "nose",
          name: "nose cone",
          shape: "cone",
          params: { radius: 2.8, height: 4.5 },
          at: [0, 11.5, 0],
          tone: ROCKET_HOT,
          note: "same radius as the body, or the socket seam puckers",
        },
        {
          id: "finA",
          name: "fin",
          shape: "pyramid",
          params: { base: 3.4, height: 3.6 },
          scale: [1, 1, 0.22],
          rz: -90,
          at: [2.6, 1.9, 0],
          tone: ROCKET_HOT,
          note: "squashed pyramids — nearly flat, so they read as panels",
        },
        {
          id: "finB",
          name: "fin",
          shape: "pyramid",
          params: { base: 3.4, height: 3.6 },
          scale: [1, 1, 0.22],
          rz: -90,
          ry: 120,
          at: [-1.3, 1.9, -2.25],
          tone: ROCKET_HOT,
        },
        {
          id: "finC",
          name: "fin",
          shape: "pyramid",
          params: { base: 3.4, height: 3.6 },
          scale: [1, 1, 0.22],
          rz: -90,
          ry: 240,
          at: [-1.3, 1.9, 2.25],
          tone: ROCKET_HOT,
        },
      ],
      attachments: [
        { joint: "socket", a: "nose", b: "body", pos: [0, 11.5, 2.2], label: "nose rim → body rim" },
        { joint: "ladder", a: "finA", b: "body", pos: [2.6, 3.2, 0], label: "fins → body (one can ride the side seam)" },
      ],
    },
    size
  );

// Registry keyed by the same ids as COMPOSITES in sewingCurriculum.js.
export const COMPOSITE_BUILDERS = {
  icecream: buildIceCream,
  mushroom: buildMushroom,
  mug: buildMug,
  microwave: buildMicrowave,
  chair: buildChair,
  rocket: buildRocket,
  teddy: buildTeddy,
};

export const buildCompositeById = (id, size = 1) => {
  const builder = COMPOSITE_BUILDERS[id];
  return builder ? builder(size) : null;
};
